import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  createContext,
  useRef,
} from "react";

import { supabase } from "./supabase";
import { getOrLinkProfile } from "./api";
import { analytics } from "./analytics";
import { setSentryUser } from "./sentry";
import { purgeCacheForDistrict } from "./cache";
import { clearRuntimeSnapshot } from "./kevia-persistence/restoreRuntimeSnapshot";

const DistrictContext = createContext(null);

// ─── localStorage helpers ───────────────────────────────────────────────────
const PROFILE_CACHE_KEY = "keves_profile_v2";
const DISTRICT_CACHE_KEY = "keves_district_v2";

function lsGet(k) {
  try { return JSON.parse(localStorage.getItem(k)); } catch { return null; }
}
function lsSet(k, v) {
  try { localStorage.setItem(k, JSON.stringify(v)); } catch {}
}
function lsClearAuth() {
  try {
    localStorage.removeItem(PROFILE_CACHE_KEY);
    localStorage.removeItem(DISTRICT_CACHE_KEY);
  } catch {}
}
// ─────────────────────────────────────────────────────────────────────────────

export function DistrictProvider({ children, onReady, user }) {
  const [district, setDistrict] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const districtRef = useRef(null);
  const userRef     = useRef(null);

  const loadWithUser = useCallback(
    async (user, silent = false, force = false) => {
      if (!silent) setLoading(true);
      if (user) userRef.current = user;

      // Safety net: if loadWithUser hangs (e.g. Dexie upgrade blocked, network
      // stall), force onReady after 10s so the SplashScreen never freezes forever.
      let resolved = false;
      let safetyTimer;
      const resolveOnce = () => {
        if (resolved) return;
        resolved = true;
        clearTimeout(safetyTimer);
        onReady?.();
      };
      safetyTimer = setTimeout(async () => {
        console.warn('[DistrictProvider] safety timeout — forcing onReady');
        if (localStorage.getItem('member_church_id')) {
          await supabase.auth.signOut();
          setUserProfile(null);
          setDistrict(null);
          districtRef.current = null;
        }
        setLoading(false);
        resolveOnce();
      }, 10000);

      // =========================
      // SIN USUARIO — logout
      // =========================
      if (!user) {
        lsClearAuth();
        clearRuntimeSnapshot();
        setDistrict(null);
        setUserProfile(null);
        districtRef.current = null;
        setSentryUser(null, null);
        setLoading(false);
        resolveOnce();
        return;
      }

      // =========================
      // MEMBER PORTAL GUARD
      // Anonymous users (signInAnonymously) are member portal users — they must
      // never be processed as pastors or have a profiles record created for them.
      // =========================
      if (user.is_anonymous) {
        setDistrict(null);
        setUserProfile(null);
        districtRef.current = null;
        setLoading(false);
        resolveOnce();
        return;
      }

      // =========================
      // PRE-CHECK: MEMBER ACCOUNT GUARD
      // Runs before getOrLinkProfile so we never create a profiles record for a
      // member-only user. Parallel queries; network failure → both null → pastor
      // proceeds normally (offline-safe).
      // =========================
      const [profileRes, memberRes] = await Promise.allSettled([
        supabase.from('profiles').select('id, role, account_status').eq('id', user.id).maybeSingle(),
        supabase.from('member_profiles').select('id').eq('user_id', user.id).maybeSingle(),
      ]);
      const profileCheck       = profileRes.status === 'fulfilled' ? profileRes.value.data  : null;
      const memberProfileCheck = memberRes.status  === 'fulfilled' ? memberRes.value.data   : null;

      if (
        memberProfileCheck &&
        (!profileCheck || profileCheck?.account_status === 'pending')
      ) {
        await supabase.auth.signOut();
        setUserProfile(null);
        setDistrict(null);
        districtRef.current = null;
        setLoading(false);
        resolveOnce();
        return;
      }

      // =========================
      // PERFIL (con fallback offline)
      // =========================
      let profile;

      try {
        profile = await getOrLinkProfile(user);
        if (profile) lsSet(PROFILE_CACHE_KEY, profile);
      } catch (err) {
        const cached = lsGet(PROFILE_CACHE_KEY);
        if (cached?.id === user.id) {
          profile = cached;
        } else {
          // Silent re-check (visibilitychange): don't wipe an already-loaded
          // district on a transient network error — loading stays false
          // throughout silent mode, so RequireDistrict would see
          // district=null + loading=false and redirect to /create-district.
          if (silent && districtRef.current) { clearTimeout(safetyTimer); return; }
          setUserProfile(null);
          setDistrict(null);
          districtRef.current = null;
          setLoading(false);
          resolveOnce();
          return;
        }
      }

      if (!profile) {
        if (silent && districtRef.current) { clearTimeout(safetyTimer); return; }
        setUserProfile(null);
        setDistrict(null);
        districtRef.current = null;
        setLoading(false);
        resolveOnce();
        return;
      }

      // Role guard: a profiles.role='member' account must never load the pastor
      // portal. Sign out immediately so RequireDistrict never sees a member profile.
      if (memberProfileCheck && profile?.account_status === 'pending') {
        await supabase.auth.signOut();
        setUserProfile(null);
        setDistrict(null);
        districtRef.current = null;
        setLoading(false);
        resolveOnce();
        return;
      }

      setUserProfile((prev) => {
        if (!force && prev?.id === profile?.id) {
          // Same user — only re-render if a routing/UI-relevant field changed.
          // Allows mid-session transfers, status changes, etc. to propagate
          // without spurious re-renders on every visibility re-check.
          if (
            prev.district_id     === profile.district_id     &&
            prev.ministry_status === profile.ministry_status &&
            prev.role            === profile.role             &&
            prev.account_status  === profile.account_status
          ) return prev;
        }
        return profile;
      });

      // =========================
      // DISTRICT CHANGE DETECTION
      // Purge stale cross-district list caches and Kevia snapshot before loading
      // the new district, so no old-district data leaks into new-district queries.
      // =========================
      const prevDistrictId = districtRef.current?.id ?? null;
      const nextDistrictId = profile.district_id ?? null;
      if (prevDistrictId && nextDistrictId && prevDistrictId !== nextDistrictId) {
        purgeCacheForDistrict(prevDistrictId).catch(() => {});
        clearRuntimeSnapshot();
      }

      // =========================
      // COORDINATOR FALLBACK
      // If apply_coordinator_invite returned false (or assign_coordinator did not
      // set district_id), the profile may have church_id set but district_id null.
      // Use the SECURITY DEFINER RPC so RLS on the churches table does not block
      // the lookup (coordinators with district_id=null cannot SELECT churches).
      // =========================
      if (profile?.role === 'coordinator' && profile.church_id && !profile.district_id) {
        try {
          const { data: districtId } = await supabase
            .rpc('get_church_district_id', { p_church_id: profile.church_id });
          if (districtId) {
            await supabase
              .from('profiles')
              .update({ district_id: districtId })
              .eq('id', profile.id);
            profile = { ...profile, district_id: districtId };
            lsSet(PROFILE_CACHE_KEY, profile);
            setUserProfile((prev) => prev?.id === profile.id ? { ...prev, district_id: districtId } : prev);
          }
        } catch {
          // Best-effort: coordinator still gets through via RequireDistrict exemption
        }
      }

      // =========================
      // SIN DISTRITO
      // =========================
      if (!profile?.district_id) {
        setDistrict(null);
        districtRef.current = null;
        setLoading(false);
        resolveOnce();
        return;
      }

      // =========================
      // CACHE EN MEMORIA
      // =========================
      if (
        districtRef.current &&
        districtRef.current.id === profile.district_id
      ) {
        setDistrict(districtRef.current);
        setLoading(false);
        resolveOnce();
        return;
      }

      // =========================
      // CARGAR DISTRITO (con fallback offline)
      // =========================
      const { data: districtData, error } = await supabase
        .from("districts")
        .select("*")
        .eq("id", profile.district_id)
        .single();

      if (error) {
        const cachedDistrict = lsGet(DISTRICT_CACHE_KEY);
        if (cachedDistrict?.id === profile.district_id) {
          setDistrict(cachedDistrict);
          districtRef.current = cachedDistrict;
        } else {
          setDistrict(null);
          districtRef.current = null;
        }
      } else {
        lsSet(DISTRICT_CACHE_KEY, districtData);
        setDistrict((prev) => {
          if (prev?.id === districtData?.id) return prev;
          return districtData;
        });
        districtRef.current = districtData;
      }

      setSentryUser(user.id, profile?.district_id || null);
      analytics.sessionStart();
      setLoading(false);
      resolveOnce();
    },
    [onReady]
  );

  // Declared before both effects that use it so declaration order matches usage.
  const lastVisibilityFetchRef = useRef(0);

  useEffect(() => {
    // Reset visibility throttle on every user change so a new session is never
    // blocked by a previous user's last fetch timestamp.
    lastVisibilityFetchRef.current = 0;
    if (!user?.id) {
      loadWithUser(null);
      return;
    }
    loadWithUser(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Re-check profile whenever the tab becomes visible again.
  // Handles the mid-session admin transfer case: if an admin moves this pastor
  // to a new district while the tab is backgrounded, the next tab focus triggers
  // a silent profile re-fetch. The smart guard above propagates the changed
  // district_id (and triggers cache purge) only when fields actually differ.
  //
  // Throttled to once per 5 minutes: on iOS the app cold-starts frequently
  // (OS kills the WKWebView under memory pressure), triggering this on every
  // resume. Without throttle it fires a Supabase round-trip on each foreground,
  // most of which are no-ops.
  useEffect(() => {
    const THROTTLE_MS = 5 * 60 * 1000;
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !userRef.current) return;
      const now = Date.now();
      if (now - lastVisibilityFetchRef.current < THROTTLE_MS) return;
      lastVisibilityFetchRef.current = now;
      loadWithUser(userRef.current, true); // silent=true, force=false
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadWithUser]);

  const isAdmin             = userProfile?.role === 'admin';
  const isCoordinator       = userProfile?.role   === 'coordinator';
  const isPastor            = !isAdmin && !isCoordinator && userProfile != null;
  const coordinatorChurchId = isCoordinator ? (userProfile?.church_id ?? null) : null;

  const refreshProfile = useCallback(() => {
    if (userRef.current) return loadWithUser(userRef.current, true, true);
  }, [loadWithUser]);

  const contextValue = useMemo(
    () => ({ district, userProfile, loading, isAdmin, isCoordinator, isPastor, coordinatorChurchId, refreshProfile, user }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [district, userProfile, loading, coordinatorChurchId, refreshProfile, user?.id, user?.is_anonymous]
  );

  return (
    <DistrictContext.Provider value={contextValue}>
      {children}
    </DistrictContext.Provider>
  );
}

export const useDistrict = () => useContext(DistrictContext);
